import React, { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Table,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Progress,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Alert,
  UncontrolledTooltip
} from 'reactstrap';
import classnames from 'classnames';
import Header from 'components/Headers/Header';
import {
  fetchExamAttempt,
  clearCurrentAttempt
} from '../../../redux/QuestionBank/FullLengthExamAction';
import { fetchSubjects, fetchUnits, fetchTopics } from '../../../redux/QuestionBank/SubjectsUnitsAction';
import { fetchPassages } from '../../../redux/QuestionBank/PassageAction';
import { draftRawToHtml } from '../../../util/draftToHtml';
import { getDisplayName } from 'util/specialtyDisplayHelper';

// ===========================
// CONSTANTS
// ===========================
const PASSING_SCORE = 70;
const TARGET_TIME_PER_QUESTION = 66; // seconds

// ===========================
// SCORE GAUGE COMPONENT
// ===========================
const ScoreGauge = ({ score: rawScore, size = 180, strokeWidth = 14 }) => {
  const score = typeof rawScore === 'number' && !isNaN(rawScore) ? rawScore : 0;

  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (score / 100) * circumference;

  const getScoreColor = () => {
    if (score >= 80) return '#2dce89';
    if (score >= 70) return '#11cdef';
    if (score >= 60) return '#fb6340';
    return '#f5365c';
  };

  const getScoreLabel = () => {
    if (score >= 80) return 'Excellent';
    if (score >= 70) return 'Passing';
    if (score >= 60) return 'Almost There';
    return 'Keep Practicing';
  };

  const isPassing = score >= PASSING_SCORE;

  return (
    <div className="text-center position-relative">
      <svg width={size} height={size} className="score-gauge">
        <circle
          stroke="#e9ecef"
          strokeWidth={strokeWidth}
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
        />
        <circle
          stroke={getScoreColor()}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          style={{
            strokeDasharray: circumference,
            strokeDashoffset: offset,
            transition: 'stroke-dashoffset 1s ease-in-out',
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%'
          }}
        />
        <text
          x="50%"
          y="46%"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: size / 4, fontWeight: 'bold', fill: getScoreColor() }}
        >
          {score}%
        </text>
        <text
          x="50%"
          y="62%"
          textAnchor="middle"
          dominantBaseline="middle"
          style={{ fontSize: size / 16, fill: '#8898aa' }}
        >
          {getScoreLabel()}
        </text>
      </svg>
      <div className="mt-2">
        <Badge color={isPassing ? 'success' : 'warning'} className="px-3 py-2">
          <i className={`fas fa-${isPassing ? 'check-circle' : 'exclamation-circle'} mr-2`}></i>
          {isPassing ? 'PASSED' : `Need ${PASSING_SCORE - score}% more to pass`}
        </Badge>
      </div>
    </div>
  );
};

// ===========================
// STAT CARD COMPONENT
// ===========================
const StatCard = ({ icon, iconBg, value, label, sublabel, smallValue }) => (
  <Card className="shadow-sm h-100">
    <CardBody className="p-3">
      <Row className="align-items-center">
        <Col xs="auto">
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center ${iconBg}`}
            style={{ width: '48px', height: '48px' }}
          >
            <i className={`${icon} text-white`} style={{ fontSize: '1.2rem' }}></i>
          </div>
        </Col>
        <Col>
          {smallValue ? (
            <h5 className="mb-0 font-weight-bold">{value}</h5>
          ) : (
            <h3 className="mb-0">{value}</h3>
          )}
          <p className="text-muted mb-0 small">{label}</p>
          {sublabel && <small className="text-muted">{sublabel}</small>}
        </Col>
      </Row>
    </CardBody>
  </Card>
);

// ===========================
// MAIN COMPONENT
// ===========================
const FullLengthExamResults = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { attemptId } = useParams();

  const { currentAttempt, attemptLoading } = useSelector(
    state => state.fullLengthExams || {}
  );
  const { subjects } = useSelector(state => state.questionBankSubjects);
  const { units } = useSelector(state => state.questionBankUnits);
  const { topics } = useSelector(state => state.questionBankTopics);
  const { passages } = useSelector(state => state.questionBankPassages);
  const activeSpecialty = useSelector(state => state.specialty?.activeSpecialty || 'pmr');

  const [activeTab, setActiveTab] = useState('overview');
  const [reviewQuestionIndex, setReviewQuestionIndex] = useState(null);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [questionFilter, setQuestionFilter] = useState('all');
  const [flaggedFilter, setFlaggedFilter] = useState(false);
  const [sectionFilter, setSectionFilter] = useState('all');

  // Pagination state
  const [questionsPage, setQuestionsPage] = useState(1);
  const itemsPerPage = 10;

  // Load data on mount
  useEffect(() => {
    dispatch(fetchExamAttempt(attemptId));
    dispatch(fetchSubjects());
    dispatch(fetchUnits());
    dispatch(fetchTopics());
    dispatch(fetchPassages());

    return () => {
      dispatch(clearCurrentAttempt());
    };
  }, [dispatch, attemptId]);

  // ===========================
  // HELPER FUNCTIONS
  // ===========================
  const getSubjectName = (subjectId) => {
    const subject = subjects?.find(s => s.subjectId === subjectId);
    return subject?.name || 'Unknown Subject';
  };

  const getSubjectCode = (subjectId) => {
    const subject = subjects?.find(s => s.subjectId === subjectId);
    return subject?.code || '';
  };

  const getUnitName = (unitId) => {
    const unit = units?.find(u => u.unitId === unitId);
    return unit?.name || 'Unknown Unit';
  };

  const getUnitCode = (unitId) => {
    const unit = units?.find(u => u.unitId === unitId);
    return unit?.code || '';
  };

  const getTopicName = (topicId) => {
    const topic = topics?.find(t => t.topicId === topicId);
    return topic?.name || 'Unknown Topic';
  };

  const getTopicCode = (topicId) => {
    const topic = topics?.find(t => t.topicId === topicId);
    return topic?.code || '';
  };

  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ===========================
  // COMPUTED VALUES
  // ===========================
  const timeUsed = useMemo(() => {
    if (!currentAttempt) return 0;
    return currentAttempt.timeLimit - (currentAttempt.timeRemaining || 0);
  }, [currentAttempt]);

  const avgTimePerQuestion = useMemo(() => {
    if (!currentAttempt?.totalQuestions) return 0;
    return Math.round(timeUsed / currentAttempt.totalQuestions);
  }, [currentAttempt, timeUsed]);

  // Performance by subject
  const performanceBySubject = useMemo(() => {
    if (!currentAttempt?.testQuestions || !currentAttempt?.selectedAnswers) return [];

    const bySubject = {};

    currentAttempt.testQuestions.forEach((question) => {
      const subjectKey = question.subjectId || question.subjectName || 'unknown';
      if (!bySubject[subjectKey]) {
        bySubject[subjectKey] = {
          subjectId: subjectKey,
          name: question.subjectId ? getSubjectName(question.subjectId) : (question.subjectName || 'Unknown Subject'),
          code: question.subjectId ? getSubjectCode(question.subjectId) : '',
          total: 0,
          correct: 0,
          incorrect: 0,
          omitted: 0,
          timeSpent: 0
        };
      }

      bySubject[subjectKey].total++;

      const selectedAnswerId = currentAttempt.selectedAnswers[question.questionId];
      const correctAnswer = question.answers?.find(a => a.isCorrect);

      if (!selectedAnswerId) {
        bySubject[subjectKey].omitted++;
      } else if (selectedAnswerId === correctAnswer?.answerId) {
        bySubject[subjectKey].correct++;
      } else {
        bySubject[subjectKey].incorrect++;
      }

      const timeSpent = currentAttempt.questionTimeTracking?.[question.questionId] || 0;
      bySubject[subjectKey].timeSpent += timeSpent;
    });

    return Object.values(bySubject)
      .map(s => ({
        ...s,
        percentage: s.total > 0 ? Math.round((s.correct / s.total) * 100) : 0
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [currentAttempt, subjects]);

  // Performance by unit
  const performanceByUnit = useMemo(() => {
    if (!currentAttempt?.testQuestions || !currentAttempt?.selectedAnswers) return [];

    const byUnit = {};

    currentAttempt.testQuestions.forEach((question) => {
      const unitKey = question.unitId || question.unitName || 'unknown';
      if (!byUnit[unitKey]) {
        byUnit[unitKey] = {
          unitId: unitKey,
          subjectId: question.subjectId || question.subjectName || null,
          name: question.unitId ? getUnitName(question.unitId) : (question.unitName || 'Unknown Unit'),
          code: question.unitId ? getUnitCode(question.unitId) : '',
          total: 0,
          correct: 0,
          incorrect: 0,
          omitted: 0,
          timeSpent: 0
        };
      }

      byUnit[unitKey].total++;

      const selectedAnswerId = currentAttempt.selectedAnswers[question.questionId];
      const correctAnswer = question.answers?.find(a => a.isCorrect);

      if (!selectedAnswerId) {
        byUnit[unitKey].omitted++;
      } else if (selectedAnswerId === correctAnswer?.answerId) {
        byUnit[unitKey].correct++;
      } else {
        byUnit[unitKey].incorrect++;
      }

      const timeSpent = currentAttempt.questionTimeTracking?.[question.questionId] || 0;
      byUnit[unitKey].timeSpent += timeSpent;
    });

    return Object.values(byUnit)
      .map(u => ({
        ...u,
        percentage: u.total > 0 ? Math.round((u.correct / u.total) * 100) : 0
      }))
      .sort((a, b) => a.percentage - b.percentage);
  }, [currentAttempt, units]);

  // Performance by topic
  const performanceByTopic = useMemo(() => {
    if (!currentAttempt?.testQuestions || !currentAttempt?.selectedAnswers) return [];

    const byTopic = {};

    currentAttempt.testQuestions.forEach((question) => {
      if (!question.topicId && !question.topicName) return; // Skip questions without topics

      const topicKey = question.topicId || question.topicName;
      if (!byTopic[topicKey]) {
        byTopic[topicKey] = {
          topicId: topicKey,
          unitId: question.unitId || question.unitName || null,
          subjectId: question.subjectId || question.subjectName || null,
          name: question.topicId ? getTopicName(question.topicId) : (question.topicName || 'Unknown Topic'),
          code: question.topicId ? getTopicCode(question.topicId) : '',
          total: 0,
          correct: 0,
          incorrect: 0,
          omitted: 0,
          timeSpent: 0
        };
      }

      byTopic[topicKey].total++;

      const selectedAnswerId = currentAttempt.selectedAnswers[question.questionId];
      const correctAnswer = question.answers?.find(a => a.isCorrect);

      if (!selectedAnswerId) {
        byTopic[topicKey].omitted++;
      } else if (selectedAnswerId === correctAnswer?.answerId) {
        byTopic[topicKey].correct++;
      } else {
        byTopic[topicKey].incorrect++;
      }

      const timeSpent = currentAttempt.questionTimeTracking?.[question.questionId] || 0;
      byTopic[topicKey].timeSpent += timeSpent;
    });

    return Object.values(byTopic)
      .map(t => ({
        ...t,
        percentage: t.total > 0 ? Math.round((t.correct / t.total) * 100) : 0
      }))
      .sort((a, b) => a.percentage - b.percentage);
  }, [currentAttempt, topics]);

  // Strengths and weaknesses
  const { strengths, weaknesses } = useMemo(() => {
    const sorted = [...performanceBySubject].sort((a, b) => b.percentage - a.percentage);
    return {
      strengths: sorted.filter(s => s.total >= 3 && s.percentage >= 70).slice(0, 3),
      weaknesses: sorted.filter(s => s.total >= 3 && s.percentage < 70).slice(-3).reverse()
    };
  }, [performanceBySubject]);

  // Get question result
  const getQuestionResult = (question) => {
    if (!currentAttempt?.selectedAnswers) return 'omitted';
    const selectedAnswerId = currentAttempt.selectedAnswers[question.questionId];
    if (!selectedAnswerId) return 'omitted';
    const correctAnswer = question.answers?.find(a => a.isCorrect);
    return selectedAnswerId === correctAnswer?.answerId ? 'correct' : 'incorrect';
  };

  // Filter questions
  const filteredQuestions = useMemo(() => {
    if (!currentAttempt?.testQuestions) return [];

    let filtered = currentAttempt.testQuestions;

    // Section filter
    if (sectionFilter !== 'all' && currentAttempt.sectionResults) {
      const section = currentAttempt.sections?.[sectionFilter];
      if (section?.questionIds) {
        const sectionIds = new Set(section.questionIds);
        filtered = filtered.filter(q => sectionIds.has(q.questionId));
      }
    }

    if (questionFilter !== 'all') {
      filtered = filtered.filter(q => getQuestionResult(q) === questionFilter);
    }

    if (flaggedFilter) {
      filtered = filtered.filter(q => currentAttempt.markedQuestions?.includes(q.questionId));
    }

    return filtered;
  }, [currentAttempt, questionFilter, flaggedFilter, sectionFilter]);

  // Render question text
  const renderQuestionText = (question) => {
    if (!question) return null;

    let html = '';
    try {
      if (typeof question.questionText === 'string') {
        const parsed = JSON.parse(question.questionText);
        html = draftRawToHtml(parsed);
      } else {
        html = draftRawToHtml(question.questionText);
      }
    } catch {
      html = question.questionText || 'Unable to display question';
    }

    return <div dangerouslySetInnerHTML={{ __html: html }} />;
  };

  // Render answer text
  const renderAnswerText = (answer) => {
    if (!answer) return '';
    if (typeof answer.answerText === 'string') {
      try {
        const parsed = JSON.parse(answer.answerText);
        return draftRawToHtml(parsed);
      } catch {
        return answer.answerText;
      }
    }
    return draftRawToHtml(answer.answerText) || '';
  };

  // Render explanation
  const renderExplanation = (question) => {
    const explanationData = question?.explanation || question?.explanationText;
    if (!explanationData) return null;

    let html = '';
    try {
      if (typeof explanationData === 'string') {
        const parsed = JSON.parse(explanationData);
        html = draftRawToHtml(parsed);
      } else {
        html = draftRawToHtml(explanationData);
      }
    } catch {
      html = typeof explanationData === 'string' ? explanationData : '';
    }

    if (!html || html === '<p></p>' || html === '<p><br></p>') return null;

    return (
      <div className="mt-4 p-3 bg-lighter rounded">
        <h6 className="mb-2"><i className="fas fa-lightbulb mr-2 text-warning"></i>Explanation</h6>
        <div dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  };

  // Open question review
  const openQuestionReview = (index) => {
    const actualIndex = currentAttempt.testQuestions.findIndex(
      q => q.questionId === filteredQuestions[index].questionId
    );
    setReviewQuestionIndex(actualIndex);
    setIsReviewModalOpen(true);
  };

  // Loading state
  if (attemptLoading || !currentAttempt) {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Row className="justify-content-center">
            <Col lg="6" md="8">
              <Card className="shadow">
                <CardBody className="text-center py-5">
                  <i className="fas fa-spinner fa-spin fa-3x text-muted mb-3"></i>
                  <h4 className="mb-0">Loading results...</h4>
                  <p className="text-muted mt-2 mb-0">Please wait while we analyze your exam performance.</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </>
    );
  }

  // Check if exam is not completed
  if (currentAttempt.status !== 'completed') {
    return (
      <>
        <Header />
        <Container className="mt--7" fluid>
          <Card className="shadow text-center">
            <CardBody className="py-5">
              <i className="fas fa-hourglass-half fa-4x text-warning mb-3"></i>
              <h3>Exam Not Completed</h3>
              <p className="text-muted">
                This exam is still in progress. Complete the exam to view results.
              </p>
              <Button
                color="primary"
                onClick={() => history.push(`/client/question-bank/full-length-exam/${attemptId}`)}
              >
                Continue Exam
              </Button>
            </CardBody>
          </Card>
        </Container>
      </>
    );
  }

  const reviewQuestion = reviewQuestionIndex !== null
    ? currentAttempt.testQuestions[reviewQuestionIndex]
    : null;

  const reviewPassage = reviewQuestion?.passageId
    ? passages?.find(p => p.passageId === reviewQuestion.passageId)
    : null;

  return (
    <>
      <Header />

      <Container className="mt--7" fluid>
        {/* Hero Score Section */}
        <Card className="shadow mb-4">
          <CardBody className="py-4">
            <Row className="align-items-center">
              <Col lg="4" className="text-center text-lg-left mb-4 mb-lg-0">
                <ScoreGauge score={currentAttempt.score} />
              </Col>
              <Col lg="8">
                <h2 className="mb-3">{currentAttempt.examName}</h2>
                <Row>
                  <Col sm="4" className="mb-3 mb-sm-0">
                    <div className="bg-success rounded p-3 text-center">
                      <h2 className="text-white mb-0">{currentAttempt.correctAnswers}</h2>
                      <small className="text-white">Correct</small>
                    </div>
                  </Col>
                  <Col sm="4" className="mb-3 mb-sm-0">
                    <div className="bg-danger rounded p-3 text-center">
                      <h2 className="text-white mb-0">{currentAttempt.incorrectAnswers || 0}</h2>
                      <small className="text-white">Incorrect</small>
                    </div>
                  </Col>
                  <Col sm="4">
                    <div className="bg-warning rounded p-3 text-center">
                      <h2 className="text-white mb-0">{currentAttempt.omittedAnswers || 0}</h2>
                      <small className="text-white">Omitted</small>
                    </div>
                  </Col>
                </Row>
                <div className="mt-3">
                  <Progress multi style={{ height: '12px' }} className="shadow">
                    <Progress
                      bar
                      color="success"
                      value={(currentAttempt.correctAnswers / currentAttempt.totalQuestions) * 100}
                    />
                    <Progress
                      bar
                      color="danger"
                      value={((currentAttempt.incorrectAnswers || 0) / currentAttempt.totalQuestions) * 100}
                    />
                    <Progress
                      bar
                      color="warning"
                      value={((currentAttempt.omittedAnswers || 0) / currentAttempt.totalQuestions) * 100}
                    />
                  </Progress>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Quick Stats */}
        <Row className="mb-4">
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <StatCard
              icon="fas fa-clock"
              iconBg="bg-primary"
              value={formatTime(timeUsed)}
              label="Time Used"
              sublabel={`of ${formatTime(currentAttempt.timeLimit)}`}
            />
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <StatCard
              icon="fas fa-stopwatch"
              iconBg="bg-info"
              value={formatTime(avgTimePerQuestion)}
              label="Avg. per Question"
              sublabel={`Target: ${formatTime(TARGET_TIME_PER_QUESTION)}`}
            />
          </Col>
          <Col lg="3" md="6" className="mb-3 mb-lg-0">
            <StatCard
              icon="fas fa-percentage"
              iconBg="bg-success"
              value={`${Math.round((currentAttempt.correctAnswers / (currentAttempt.totalQuestions - (currentAttempt.omittedAnswers || 0))) * 100) || 0}%`}
              label="Accuracy Rate"
              sublabel="(excluding omitted)"
            />
          </Col>
          <Col lg="3" md="6">
            <StatCard
              icon="fas fa-calendar-check"
              iconBg="bg-warning"
              value={formatDate(currentAttempt.completedAt)}
              label="Completed"
              smallValue
            />
          </Col>
        </Row>

        {/* Navigation Tabs */}
        <Nav tabs className="mb-4">
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'overview' })}
              onClick={() => setActiveTab('overview')}
              style={{ cursor: 'pointer' }}
            >
              <i className="fas fa-chart-pie mr-2"></i>
              Overview
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'subjects' })}
              onClick={() => setActiveTab('subjects')}
              style={{ cursor: 'pointer' }}
            >
              <i className="fas fa-book mr-2"></i>
              Subjects
            </NavLink>
          </NavItem>
          {currentAttempt?.sectionResults && (
            <NavItem>
              <NavLink
                className={classnames({ active: activeTab === 'sections' })}
                onClick={() => setActiveTab('sections')}
                style={{ cursor: 'pointer' }}
              >
                <i className="fas fa-layer-group mr-2"></i>
                Sections
              </NavLink>
            </NavItem>
          )}
          <NavItem>
            <NavLink
              className={classnames({ active: activeTab === 'questions' })}
              onClick={() => setActiveTab('questions')}
              style={{ cursor: 'pointer' }}
            >
              <i className="fas fa-list-ol mr-2"></i>
              Questions
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={activeTab}>
          {/* ===========================
              OVERVIEW TAB
              =========================== */}
          <TabPane tabId="overview">
            <Row>
              {/* Strengths & Weaknesses */}
              <Col lg="6" className="mb-4">
                <Card className="shadow h-100">
                  <CardHeader className="bg-transparent">
                    <h5 className="mb-0">
                      <i className="fas fa-trophy text-success mr-2"></i>
                      Strengths
                      <i className="fas fa-info-circle text-muted ml-2" id="strengthsInfo" style={{ fontSize: '0.75rem', cursor: 'pointer' }}></i>
                      <UncontrolledTooltip target="strengthsInfo" placement="top">
                        Subjects with 70% or above
                      </UncontrolledTooltip>
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {strengths.length === 0 ? (
                      <p className="text-muted text-center mb-0">
                        <i className="fas fa-info-circle mr-1"></i>
                        Keep practicing to identify strengths
                      </p>
                    ) : (
                      strengths.map((s, idx) => (
                        <div key={s.subjectId} className="d-flex align-items-center mb-3 p-2 bg-lighter rounded">
                          <Badge color="success" className="mr-3">{idx + 1}</Badge>
                          <span className="flex-grow-1">{getDisplayName(s, activeSpecialty)}</span>
                          <Badge color="success" pill className="px-3">{s.percentage}%</Badge>
                        </div>
                      ))
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" className="mb-4">
                <Card className="shadow h-100">
                  <CardHeader className="bg-transparent">
                    <h5 className="mb-0">
                      <i className="fas fa-exclamation-triangle text-warning mr-2"></i>
                      Areas for Improvement
                      <i className="fas fa-info-circle text-muted ml-2" id="weaknessesInfo" style={{ fontSize: '0.75rem', cursor: 'pointer' }}></i>
                      <UncontrolledTooltip target="weaknessesInfo" placement="top">
                        Subjects below 70%
                      </UncontrolledTooltip>
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {weaknesses.length === 0 ? (
                      <p className="text-muted text-center mb-0">
                        <i className="fas fa-check-circle text-success mr-1"></i>
                        Great job! No significant weaknesses
                      </p>
                    ) : (
                      weaknesses.map((s, idx) => (
                        <div key={s.subjectId} className="d-flex align-items-center mb-3 p-2 bg-lighter rounded">
                          <Badge color="danger" className="mr-3">{idx + 1}</Badge>
                          <span className="flex-grow-1">{getDisplayName(s, activeSpecialty)}</span>
                          <Badge color="danger" pill className="px-3">{s.percentage}%</Badge>
                        </div>
                      ))
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Subject Performance Summary */}
            <Card className="shadow mb-4">
              <CardHeader className="bg-transparent">
                <h4 className="mb-0">
                  <i className="fas fa-chart-bar text-primary mr-2"></i>
                  Subject Performance
                </h4>
              </CardHeader>
              <CardBody>
                {performanceBySubject.map((subject) => {
                  const color = subject.percentage >= PASSING_SCORE ? 'success' : subject.percentage >= 50 ? 'warning' : 'danger';

                  return (
                    <div key={subject.subjectId} className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="font-weight-bold">
                          {getDisplayName(subject, activeSpecialty)}
                        </span>
                        <div>
                          <small className="text-muted mr-2">{subject.correct}/{subject.total}</small>
                          <Badge color={color}>{subject.percentage}%</Badge>
                        </div>
                      </div>
                      <Progress value={subject.percentage} color={color} style={{ height: '8px' }} />
                    </div>
                  );
                })}
              </CardBody>
            </Card>
          </TabPane>

          {/* ===========================
              SUBJECTS TAB
              =========================== */}
          <TabPane tabId="subjects">
            {/* Subject Performance Summary */}
            <Row className="mb-4">
              <Col lg="4" md="6" className="mb-3 mb-lg-0">
                <Card className="shadow h-100 border-left-success" style={{ borderLeft: '4px solid #2dce89' }}>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="text-muted text-uppercase font-weight-bold mb-1" style={{ fontSize: '11px' }}>
                          Passing Subjects
                        </p>
                        <h2 className="text-success mb-0">
                          {performanceBySubject.filter(s => s.percentage >= PASSING_SCORE).length}
                          <small className="text-muted" style={{ fontSize: '14px' }}> / {performanceBySubject.length}</small>
                        </h2>
                      </div>
                      <div className="text-success">
                        <i className="fas fa-check-circle fa-2x"></i>
                      </div>
                    </div>
                    <small className="text-muted">Scored {PASSING_SCORE}% or higher</small>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="4" md="6" className="mb-3 mb-lg-0">
                <Card className="shadow h-100 border-left-warning" style={{ borderLeft: '4px solid #fb6340' }}>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="text-muted text-uppercase font-weight-bold mb-1" style={{ fontSize: '11px' }}>
                          Needs Improvement
                        </p>
                        <h2 className="text-warning mb-0">
                          {performanceBySubject.filter(s => s.percentage >= 50 && s.percentage < PASSING_SCORE).length}
                          <small className="text-muted" style={{ fontSize: '14px' }}> / {performanceBySubject.length}</small>
                        </h2>
                      </div>
                      <div className="text-warning">
                        <i className="fas fa-exclamation-triangle fa-2x"></i>
                      </div>
                    </div>
                    <small className="text-muted">Scored 50-{PASSING_SCORE - 1}%</small>
                  </CardBody>
                </Card>
              </Col>
              <Col lg="4" md="6" className="mb-3 mb-lg-0">
                <Card className="shadow h-100 border-left-danger" style={{ borderLeft: '4px solid #f5365c' }}>
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <p className="text-muted text-uppercase font-weight-bold mb-1" style={{ fontSize: '11px' }}>
                          Critical Areas
                        </p>
                        <h2 className="text-danger mb-0">
                          {performanceBySubject.filter(s => s.percentage < 50).length}
                          <small className="text-muted" style={{ fontSize: '14px' }}> / {performanceBySubject.length}</small>
                        </h2>
                      </div>
                      <div className="text-danger">
                        <i className="fas fa-times-circle fa-2x"></i>
                      </div>
                    </div>
                    <small className="text-muted">Scored below 50%</small>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            {/* Subject Detailed Cards with Units */}
            <Card className="shadow mb-4">
              <CardHeader className="bg-transparent">
                <h4 className="mb-0">
                  <i className="fas fa-layer-group text-info mr-2"></i>
                  Subject Details with Unit Breakdown
                </h4>
              </CardHeader>
              <CardBody>
                {performanceBySubject.map((subject, idx) => {
                  const subjectUnits = performanceByUnit.filter(u => u.subjectId === subject.subjectId);
                  const color = subject.percentage >= PASSING_SCORE ? 'success' : subject.percentage >= 50 ? 'warning' : 'danger';
                  const avgTimePerQ = subject.total > 0 ? Math.round(subject.timeSpent / subject.total) : 0;
                  const pointsToPass = subject.percentage < PASSING_SCORE ? Math.ceil((PASSING_SCORE / 100 * subject.total) - subject.correct) : 0;

                  return (
                    <div key={subject.subjectId} className={`mb-4 pb-4 ${idx < performanceBySubject.length - 1 ? 'border-bottom' : ''}`}>
                      {/* Subject Header */}
                      <Row className="align-items-center mb-3">
                        <Col md="6">
                          <div className="d-flex align-items-center">
                            <div
                              className={`rounded-circle bg-${color} d-flex align-items-center justify-content-center mr-3`}
                              style={{ width: 50, height: 50 }}
                            >
                              <span className="text-white font-weight-bold">{subject.percentage}%</span>
                            </div>
                            <div>
                              <h5 className="mb-0">{getDisplayName(subject, activeSpecialty)}</h5>
                              <small className="text-muted">{subject.total} questions • {subjectUnits.length} units</small>
                            </div>
                          </div>
                        </Col>
                        <Col md="6" className="text-md-right mt-2 mt-md-0">
                          {pointsToPass > 0 ? (
                            <Badge color="warning" className="mr-2">
                              <i className="fas fa-arrow-up mr-1"></i>
                              Need {pointsToPass} more correct to pass
                            </Badge>
                          ) : (
                            <Badge color="success" className="mr-2">
                              <i className="fas fa-check mr-1"></i>
                              Passing
                            </Badge>
                          )}
                        </Col>
                      </Row>

                      {/* Subject Stats */}
                      <Row className="mb-3">
                        <Col xs="6" sm="3" className="mb-2 mb-sm-0">
                          <div className="p-2 bg-lighter rounded text-center">
                            <h5 className="text-success mb-0">{subject.correct}</h5>
                            <small className="text-muted">Correct</small>
                          </div>
                        </Col>
                        <Col xs="6" sm="3" className="mb-2 mb-sm-0">
                          <div className="p-2 bg-lighter rounded text-center">
                            <h5 className="text-danger mb-0">{subject.incorrect}</h5>
                            <small className="text-muted">Incorrect</small>
                          </div>
                        </Col>
                        <Col xs="6" sm="3">
                          <div className="p-2 bg-lighter rounded text-center">
                            <h5 className="text-warning mb-0">{subject.omitted}</h5>
                            <small className="text-muted">Omitted</small>
                          </div>
                        </Col>
                        <Col xs="6" sm="3">
                          <div className="p-2 bg-lighter rounded text-center">
                            <h5 className="text-info mb-0">{avgTimePerQ}s</h5>
                            <small className="text-muted">Avg Time</small>
                          </div>
                        </Col>
                      </Row>

                      {/* Units Table */}
                      {subjectUnits.length > 0 && (
                        <div className="table-responsive">
                          <Table size="sm" className="mb-0">
                            <thead className="bg-lighter">
                              <tr>
                                <th style={{ fontSize: '12px' }}>Unit / Topic</th>
                                <th className="text-center" style={{ fontSize: '12px', width: '80px' }}>Score</th>
                                <th className="text-center" style={{ fontSize: '12px', width: '100px' }}>Correct</th>
                                <th style={{ fontSize: '12px', width: '150px' }}>Performance</th>
                              </tr>
                            </thead>
                            <tbody>
                              {subjectUnits
                                .sort((a, b) => a.percentage - b.percentage)
                                .map((unit) => {
                                  const unitColor = unit.percentage >= PASSING_SCORE ? 'success' : unit.percentage >= 50 ? 'warning' : 'danger';
                                  const unitTopics = performanceByTopic.filter(t => t.unitId === unit.unitId);
                                  return (
                                    <React.Fragment key={unit.unitId}>
                                      <tr>
                                        <td>
                                          <span style={{ fontSize: '13px' }} className="font-weight-bold">{getDisplayName(unit, activeSpecialty)}</span>
                                        </td>
                                        <td className="text-center">
                                          <Badge color={unitColor} style={{ fontSize: '11px', minWidth: '40px' }}>
                                            {unit.percentage}%
                                          </Badge>
                                        </td>
                                        <td className="text-center" style={{ fontSize: '12px' }}>
                                          <span className="text-success">{unit.correct}</span>
                                          <span className="text-muted">/</span>
                                          <span>{unit.total}</span>
                                        </td>
                                        <td>
                                          <Progress value={unit.percentage} color={unitColor} style={{ height: '6px' }} />
                                        </td>
                                      </tr>
                                      {unitTopics
                                        .sort((a, b) => a.percentage - b.percentage)
                                        .map((topic) => {
                                          const topicColor = topic.percentage >= PASSING_SCORE ? 'success' : topic.percentage >= 50 ? 'warning' : 'danger';
                                          return (
                                            <tr key={topic.topicId} style={{ backgroundColor: '#f8f9fe' }}>
                                              <td style={{ paddingLeft: '28px' }}>
                                                <span style={{ fontSize: '12px' }} className="text-muted">
                                                  <i className="fas fa-angle-right mr-1" style={{ fontSize: '10px' }}></i>
                                                  {getDisplayName(topic, activeSpecialty)}
                                                </span>
                                              </td>
                                              <td className="text-center">
                                                <Badge color={topicColor} style={{ fontSize: '10px', minWidth: '36px' }}>
                                                  {topic.percentage}%
                                                </Badge>
                                              </td>
                                              <td className="text-center" style={{ fontSize: '11px' }}>
                                                <span className="text-success">{topic.correct}</span>
                                                <span className="text-muted">/</span>
                                                <span>{topic.total}</span>
                                              </td>
                                              <td>
                                                <Progress value={topic.percentage} color={topicColor} style={{ height: '4px' }} />
                                              </td>
                                            </tr>
                                          );
                                        })}
                                    </React.Fragment>
                                  );
                                })}
                            </tbody>
                          </Table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardBody>
            </Card>

            {/* Study Priority */}
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <h4 className="mb-0">
                  <i className="fas fa-bullseye text-danger mr-2"></i>
                  Focus Areas
                </h4>
                <small className="text-muted">Units and topics that need the most attention</small>
              </CardHeader>
              <CardBody>
                {(() => {
                  const priorityUnits = performanceByUnit
                    .filter(u => u.percentage < PASSING_SCORE)
                    .map(u => ({
                      ...u,
                      potentialGain: Math.ceil((PASSING_SCORE / 100 * u.total) - u.correct)
                    }))
                    .sort((a, b) => a.percentage - b.percentage)
                    .slice(0, 5);

                  if (priorityUnits.length === 0) {
                    return (
                      <div className="text-center py-4">
                        <i className="fas fa-trophy fa-3x text-success mb-3"></i>
                        <h5 className="text-success">Excellent!</h5>
                        <p className="text-muted mb-0">All units are at or above passing threshold.</p>
                      </div>
                    );
                  }

                  return (
                    <>
                      <Alert color="info" className="mb-3">
                        <i className="fas fa-info-circle mr-2"></i>
                        Focus on these units first for maximum score improvement.
                      </Alert>
                      {priorityUnits.map((unit, idx) => {
                        const unitTopics = performanceByTopic
                          .filter(t => t.unitId === unit.unitId && t.percentage < PASSING_SCORE)
                          .sort((a, b) => a.percentage - b.percentage);

                        return (
                          <div key={unit.unitId} className="mb-3">
                            <div className="d-flex align-items-center p-3 bg-lighter rounded">
                              <div
                                className="rounded-circle bg-danger d-flex align-items-center justify-content-center mr-3"
                                style={{ width: 36, height: 36, minWidth: 36 }}
                              >
                                <span className="text-white font-weight-bold" style={{ fontSize: '14px' }}>#{idx + 1}</span>
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <span className="font-weight-bold">{getDisplayName(unit, activeSpecialty)}</span>
                                    <small className="text-muted ml-2">({getSubjectName(unit.subjectId)})</small>
                                  </div>
                                  <Badge color="danger">{unit.percentage}%</Badge>
                                </div>
                                <small className="text-muted">
                                  {unit.correct}/{unit.total} correct •
                                  <span className="text-success ml-1">+{unit.potentialGain} needed to pass</span>
                                </small>
                              </div>
                            </div>
                            {unitTopics.length > 0 && (
                              <div className="ml-4 mt-1">
                                {unitTopics.map((topic) => {
                                  const topicColor = topic.percentage >= 50 ? 'warning' : 'danger';
                                  return (
                                    <div key={topic.topicId} className="d-flex align-items-center py-2 px-3 border-left" style={{ borderLeftWidth: '2px', borderLeftColor: '#f5365c' }}>
                                      <i className="fas fa-angle-right text-muted mr-2" style={{ fontSize: '11px' }}></i>
                                      <div className="flex-grow-1">
                                        <div className="d-flex justify-content-between align-items-center">
                                          <span style={{ fontSize: '13px' }}>{getDisplayName(topic, activeSpecialty)}</span>
                                          <Badge color={topicColor} style={{ fontSize: '11px' }}>{topic.percentage}%</Badge>
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '11px' }}>
                                          {topic.correct}/{topic.total} correct
                                        </small>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  );
                })()}
              </CardBody>
            </Card>
          </TabPane>

          {/* ===========================
              SECTIONS TAB
              =========================== */}
          {currentAttempt?.sectionResults && (
            <TabPane tabId="sections">
              <Row>
                {currentAttempt.sectionResults.map((section) => {
                  const timeUsedSec = section.timeLimitSeconds - (section.timeRemaining || 0);
                  const scoreColor = section.score >= 80 ? '#2dce89' : section.score >= 70 ? '#11cdef' : section.score >= 60 ? '#fb6340' : '#f5365c';
                  return (
                    <Col lg="6" key={section.sectionIndex} className="mb-4">
                      <Card className="shadow h-100">
                        <CardHeader className="bg-transparent d-flex justify-content-between align-items-center">
                          <h4 className="mb-0">{section.name}</h4>
                          <Badge color={section.score >= PASSING_SCORE ? 'success' : 'danger'} className="px-3 py-2" style={{ fontSize: '1rem' }}>
                            {section.score}%
                          </Badge>
                        </CardHeader>
                        <CardBody>
                          <div className="mb-3">
                            <div className="d-flex justify-content-between mb-1">
                              <small className="text-muted">Score</small>
                              <small style={{ color: scoreColor, fontWeight: '600' }}>{section.score}%</small>
                            </div>
                            <Progress value={section.score} color={section.score >= 70 ? 'success' : section.score >= 50 ? 'warning' : 'danger'} style={{ height: '8px' }} />
                          </div>

                          <Row className="text-center mb-3">
                            <Col xs="4">
                              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#2dce89' }}>{section.correct}</div>
                              <small className="text-muted">Correct</small>
                            </Col>
                            <Col xs="4">
                              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#f5365c' }}>{section.incorrect}</div>
                              <small className="text-muted">Incorrect</small>
                            </Col>
                            <Col xs="4">
                              <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#fb6340' }}>{section.omitted}</div>
                              <small className="text-muted">Omitted</small>
                            </Col>
                          </Row>

                          <div className="d-flex justify-content-between mb-1">
                            <small className="text-muted">Questions</small>
                            <small>{section.questionCount}</small>
                          </div>
                          <div className="d-flex justify-content-between mb-1">
                            <small className="text-muted">Time Used</small>
                            <small>{formatTime(timeUsedSec)} / {formatTime(section.timeLimitSeconds)}</small>
                          </div>
                          <div className="d-flex justify-content-between">
                            <small className="text-muted">Avg. per Question</small>
                            <small>{section.questionCount > 0 ? formatTime(Math.round(timeUsedSec / section.questionCount)) : '--'}</small>
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </TabPane>
          )}

          {/* ===========================
              QUESTIONS TAB
              =========================== */}
          <TabPane tabId="questions">
            <Card className="shadow">
              <CardHeader className="bg-transparent">
                <Row className="align-items-center">
                  <Col md="12">
                    <h4 className="mb-3">Question Review</h4>
                    <div className="d-flex flex-wrap">
                      {/* Result Filter */}
                      <div className="mr-3 mb-2">
                        <small className="text-muted d-block mb-1">Result:</small>
                        <Button
                          color={questionFilter === 'all' ? 'primary' : 'secondary'}
                          size="sm"
                          className="mr-1"
                          onClick={() => { setQuestionFilter('all'); setQuestionsPage(1); }}
                        >
                          All ({currentAttempt.totalQuestions})
                        </Button>
                        <Button
                          color={questionFilter === 'correct' ? 'success' : 'secondary'}
                          size="sm"
                          className="mr-1"
                          onClick={() => { setQuestionFilter('correct'); setQuestionsPage(1); }}
                        >
                          <i className="fas fa-check mr-1"></i>
                          {currentAttempt.correctAnswers}
                        </Button>
                        <Button
                          color={questionFilter === 'incorrect' ? 'danger' : 'secondary'}
                          size="sm"
                          className="mr-1"
                          onClick={() => { setQuestionFilter('incorrect'); setQuestionsPage(1); }}
                        >
                          <i className="fas fa-times mr-1"></i>
                          {currentAttempt.incorrectAnswers || 0}
                        </Button>
                        <Button
                          color={questionFilter === 'omitted' ? 'warning' : 'secondary'}
                          size="sm"
                          onClick={() => { setQuestionFilter('omitted'); setQuestionsPage(1); }}
                        >
                          <i className="fas fa-minus mr-1"></i>
                          {currentAttempt.omittedAnswers || 0}
                        </Button>
                      </div>

                      {/* Section Filter */}
                      {currentAttempt?.sectionResults && (
                        <div className="mr-3 mb-2">
                          <small className="text-muted d-block mb-1">Section:</small>
                          <Button
                            color={sectionFilter === 'all' ? 'primary' : 'secondary'}
                            size="sm"
                            className="mr-1"
                            onClick={() => { setSectionFilter('all'); setQuestionsPage(1); }}
                          >
                            All
                          </Button>
                          {currentAttempt.sectionResults.map((s) => (
                            <Button
                              key={s.sectionIndex}
                              color={sectionFilter === s.sectionIndex ? 'primary' : 'secondary'}
                              size="sm"
                              className="mr-1"
                              onClick={() => { setSectionFilter(s.sectionIndex); setQuestionsPage(1); }}
                            >
                              {s.name}
                            </Button>
                          ))}
                        </div>
                      )}

                      {/* Flagged Filter */}
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Flagged:</small>
                        <Button
                          color={flaggedFilter ? 'info' : 'secondary'}
                          size="sm"
                          onClick={() => { setFlaggedFilter(!flaggedFilter); setQuestionsPage(1); }}
                        >
                          <i className="fas fa-flag mr-1"></i>
                          {flaggedFilter ? 'Showing Flagged' : 'All'} ({currentAttempt.markedQuestions?.length || 0})
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <div className="table-responsive">
                  <Table className="align-items-center table-flush" hover>
                    <thead className="thead-light">
                      <tr>
                        <th>Result</th>
                        <th>#</th>
                        <th>Subject</th>
                        <th>Unit</th>
                        <th>Topic</th>
                        <th className="text-center">Time Spent</th>
                        <th className="text-center">Flagged</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredQuestions
                        .slice((questionsPage - 1) * itemsPerPage, questionsPage * itemsPerPage)
                        .map((question, idx) => {
                          const result = getQuestionResult(question);
                          const timeSpent = currentAttempt.questionTimeTracking?.[question.questionId] || 0;
                          const isMarked = currentAttempt.markedQuestions?.includes(question.questionId);
                          const originalIndex = currentAttempt.testQuestions.findIndex(
                            q => q.questionId === question.questionId
                          );
                          const actualIdx = (questionsPage - 1) * itemsPerPage + idx;

                          // Get full names with codes
                          const subjectCode = getSubjectCode(question.subjectId);
                          const subjectName = getSubjectName(question.subjectId);
                          const subjectDisplay = subjectCode ? `${subjectCode} ${subjectName}` : subjectName;

                          const unitCode = getUnitCode(question.unitId);
                          const unitName = getUnitName(question.unitId);
                          const unitDisplay = unitCode ? `${unitCode} ${unitName}` : unitName;

                          const topicCode = question.topicId ? getTopicCode(question.topicId) : '';
                          const topicName = question.topicId ? getTopicName(question.topicId) : null;
                          const topicDisplay = topicName ? (topicCode ? `${topicCode} ${topicName}` : topicName) : null;

                          return (
                            <tr key={question.questionId}>
                              <td>
                                {result === 'correct' ? (
                                  <span className="font-weight-bold text-success" style={{ fontSize: '1rem' }}>
                                    <i className="fas fa-check mr-1"></i>Correct
                                  </span>
                                ) : result === 'incorrect' ? (
                                  <span className="font-weight-bold text-danger" style={{ fontSize: '1rem' }}>
                                    <i className="fas fa-times mr-1"></i>Incorrect
                                  </span>
                                ) : (
                                  <Badge color="warning" pill>Omitted</Badge>
                                )}
                              </td>
                              <td>
                                <span className="font-weight-bold">{originalIndex + 1}</span>
                              </td>
                              <td>
                                <Badge color="primary" pill>
                                  {subjectDisplay}
                                </Badge>
                              </td>
                              <td>
                                <Badge color="info" pill>
                                  {unitDisplay}
                                </Badge>
                              </td>
                              <td>
                                {topicDisplay ? (
                                  <Badge color="success" pill>
                                    {topicDisplay}
                                  </Badge>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td className="text-center">
                                <span className={timeSpent > TARGET_TIME_PER_QUESTION ? 'text-warning' : ''}>
                                  {formatTime(timeSpent)}
                                </span>
                              </td>
                              <td className="text-center">
                                {isMarked ? (
                                  <i className="fas fa-flag text-warning" title="Flagged"></i>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm" role="group">
                                  <Button
                                    color="info"
                                    size="sm"
                                    onClick={() => openQuestionReview(actualIdx)}
                                    title="Review Question"
                                  >
                                    <i className="fas fa-eye"></i>
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
              {filteredQuestions.length > 0 && (
                <CardFooter className="py-4">
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center">
                    <div className="text-muted small mb-2 mb-sm-0">
                      Showing {((questionsPage - 1) * itemsPerPage) + 1} to {Math.min(questionsPage * itemsPerPage, filteredQuestions.length)} of {filteredQuestions.length} questions
                    </div>
                    <div className="d-flex align-items-center">
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setQuestionsPage(p => Math.max(1, p - 1))}
                        disabled={questionsPage === 1 || Math.ceil(filteredQuestions.length / itemsPerPage) <= 1}
                        className="mr-2"
                      >
                        <i className="fas fa-chevron-left"></i>
                        <span className="d-none d-sm-inline ml-1">Previous</span>
                      </Button>
                      <span className="mx-2 text-nowrap">
                        Page {questionsPage} of {Math.ceil(filteredQuestions.length / itemsPerPage)}
                      </span>
                      <Button
                        color="primary"
                        size="sm"
                        onClick={() => setQuestionsPage(p => Math.min(Math.ceil(filteredQuestions.length / itemsPerPage), p + 1))}
                        disabled={questionsPage === Math.ceil(filteredQuestions.length / itemsPerPage) || Math.ceil(filteredQuestions.length / itemsPerPage) <= 1}
                        className="ml-2"
                      >
                        <span className="d-none d-sm-inline mr-1">Next</span>
                        <i className="fas fa-chevron-right"></i>
                      </Button>
                    </div>
                  </div>
                </CardFooter>
              )}
            </Card>
          </TabPane>
        </TabContent>

        {/* Back Button */}
        <div className="mt-4 mb-5">
          <Button
            color="secondary"
            onClick={() => history.push('/client/question-bank/full-length-exams')}
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Exams
          </Button>
        </div>
      </Container>

      {/* Question Review Modal */}
      <Modal isOpen={isReviewModalOpen} toggle={() => setIsReviewModalOpen(false)} size="xl">
        <ModalHeader toggle={() => setIsReviewModalOpen(false)}>
          <span className="mr-3">Question {reviewQuestionIndex !== null ? reviewQuestionIndex + 1 : ''}</span>
          {reviewQuestion && (
            <>
              <Badge
                color={
                  getQuestionResult(reviewQuestion) === 'correct' ? 'success' :
                  getQuestionResult(reviewQuestion) === 'incorrect' ? 'danger' : 'secondary'
                }
                className="mr-2"
              >
                {getQuestionResult(reviewQuestion).charAt(0).toUpperCase() + getQuestionResult(reviewQuestion).slice(1)}
              </Badge>
              <Badge color="info">
                <i className="fas fa-clock mr-1"></i>
                {formatTime(currentAttempt.questionTimeTracking?.[reviewQuestion.questionId] || 0)}
              </Badge>
            </>
          )}
        </ModalHeader>
        <ModalBody className="exam-content-font">
          {reviewQuestion && (
            <Row>
              {/* Passage (if exists) */}
              {reviewPassage && (
                <Col lg="5" className="border-right" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <h6 className="mb-2">
                    <Badge color="info" className="mr-2">Passage</Badge>
                    {reviewPassage.title}
                  </h6>
                  <hr />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: (() => {
                        try {
                          if (typeof reviewPassage.passageText === 'string') {
                            return draftRawToHtml(JSON.parse(reviewPassage.passageText));
                          }
                          return draftRawToHtml(reviewPassage.passageText);
                        } catch {
                          return reviewPassage.passageText || '';
                        }
                      })()
                    }}
                  />
                </Col>
              )}

              {/* Question and Answers */}
              <Col lg={reviewPassage ? '7' : '12'}>
                <div className="mb-3">
                  <Badge color="primary" className="mr-1">
                    {reviewQuestion.subjectId && getSubjectCode(reviewQuestion.subjectId)
                      ? `${getSubjectCode(reviewQuestion.subjectId)} ${getSubjectName(reviewQuestion.subjectId)}`
                      : reviewQuestion.subjectName || (reviewQuestion.subjectId ? getSubjectName(reviewQuestion.subjectId) : 'N/A')}
                  </Badge>
                  <Badge color="info" className="mr-1">
                    {reviewQuestion.unitId && getUnitCode(reviewQuestion.unitId)
                      ? `${getUnitCode(reviewQuestion.unitId)} ${getUnitName(reviewQuestion.unitId)}`
                      : reviewQuestion.unitName || (reviewQuestion.unitId ? getUnitName(reviewQuestion.unitId) : 'N/A')}
                  </Badge>
                  {(reviewQuestion.topicId || reviewQuestion.topicName) && (
                    <Badge color="success">
                      {reviewQuestion.topicId && getTopicCode(reviewQuestion.topicId)
                        ? `${getTopicCode(reviewQuestion.topicId)} ${getTopicName(reviewQuestion.topicId)}`
                        : reviewQuestion.topicName || (reviewQuestion.topicId ? getTopicName(reviewQuestion.topicId) : '')}
                    </Badge>
                  )}
                </div>

                {/* Question Text */}
                <div className="mb-4 p-3 bg-lighter rounded">
                  {renderQuestionText(reviewQuestion)}
                </div>

                {/* Answer Options */}
                {reviewQuestion.answers?.map((answer, idx) => {
                  const letter = String.fromCharCode(65 + idx);
                  const isCorrect = answer.isCorrect;
                  const isSelected = currentAttempt.selectedAnswers?.[reviewQuestion.questionId] === answer.answerId;

                  let bgColor = 'white';
                  let borderColor = '#dee2e6';

                  if (isCorrect) {
                    bgColor = '#d4edda';
                    borderColor = '#28a745';
                  } else if (isSelected && !isCorrect) {
                    bgColor = '#f8d7da';
                    borderColor = '#dc3545';
                  }

                  return (
                    <div
                      key={answer.answerId || idx}
                      className="p-3 mb-2 rounded"
                      style={{
                        backgroundColor: bgColor,
                        border: `2px solid ${borderColor}`
                      }}
                    >
                      <div className="d-flex align-items-start">
                        <div
                          className={`mr-3 rounded-circle d-flex align-items-center justify-content-center ${isCorrect ? 'bg-success text-white' : isSelected ? 'bg-danger text-white' : 'bg-lighter'}`}
                          style={{ width: '32px', height: '32px', minWidth: '32px' }}
                        >
                          <strong>{letter}</strong>
                        </div>
                        <div
                          className="flex-grow-1"
                          dangerouslySetInnerHTML={{ __html: renderAnswerText(answer) }}
                        />
                        <div className="ml-2">
                          {isCorrect && <i className="fas fa-check text-success"></i>}
                          {isSelected && !isCorrect && <i className="fas fa-times text-danger"></i>}
                          {isSelected && <small className="text-muted ml-1">(Your answer)</small>}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Explanation */}
                {renderExplanation(reviewQuestion)}
              </Col>
            </Row>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            disabled={reviewQuestionIndex === 0}
            onClick={() => setReviewQuestionIndex(prev => prev - 1)}
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Previous
          </Button>
          <span className="mx-3 text-muted">
            {reviewQuestionIndex !== null ? reviewQuestionIndex + 1 : 0} / {currentAttempt.testQuestions?.length || 0}
          </span>
          <Button
            color="secondary"
            disabled={reviewQuestionIndex === (currentAttempt.testQuestions?.length || 0) - 1}
            onClick={() => setReviewQuestionIndex(prev => prev + 1)}
          >
            Next
            <i className="fas fa-arrow-right ml-1"></i>
          </Button>
          <Button color="primary" onClick={() => setIsReviewModalOpen(false)}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default FullLengthExamResults;
